/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Private Service Connect", description = "Securely connect your warehouse to Hightouch using Google Cloud Private Service Connect.", lastUpdatedDate = new Date(1715731200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Private Service Connect",
        description: "Securely connect your warehouse to Hightouch using Google Cloud Private Service Connect.",
        lastUpdatedDate: "Wed May 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Cloud SQL",
          "depth": 0,
          "id": "cloud-sql"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {BtOnly, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Private Service Connect"
      }), "\n", _jsxs(_components.p, {
        children: ["Using ", _jsx(_components.a, {
          href: "https://cloud.google.com/vpc/docs/private-service-connect",
          children: "Private Service Connect"
        }), ", you can securely connect your Google Cloud-deployed services to Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "Network traffic between Hightouch and your services connected by Private Service Connect stays on the Google Cloud network and doesn't travel over the public internet. That means that you can restrict public access to your services, and your data is never exposed to the public internet."
      }), "\n", _jsx(_components.h2, {
        id: "cloud-sql",
        children: "Cloud SQL"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports Google Cloud Private Service Connect for Cloud SQL instances (MySQL, Postgres, SQL Server)."
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports GCP Private Service Connect on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be in the GCP ", _jsx(_components.code, {
            children: "us-east4"
          }), " Hightouch region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Cloud SQL instance must be in the GCP ", _jsx(_components.code, {
            children: "us-east1"
          }), ", ", _jsx(_components.code, {
            children: "us-east4"
          }), ", or ", _jsx(_components.code, {
            children: "us-west2"
          }), " regions. If you're interested in using a different region, please reach out to our customer success team."]
        }), "\n", _jsx(_components.li, {
          children: "Your Cloud SQL instance must have Private Service Connect enabled."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["You will need to enable Private Service Connect on your Cloud SQL instance. You can do this by following the documentation for ", _jsx(_components.a, {
          href: "https://cloud.google.com/sql/docs/mysql/private-service-connect",
          children: "MySQL"
        }), ", ", _jsx(_components.a, {
          href: "https://cloud.google.com/sql/docs/postgres/private-service-connect",
          children: "PostgreSQL"
        }), ", or ", _jsx(_components.a, {
          href: "https://cloud.google.com/sql/docs/sqlserver/configure-private-service-connect#create-cloud-sql-instance-psc-enabled",
          children: "SQL Server"
        }), ". You will also need to authorize the Hightouch Google Cloud Project to create a Private Service Connect endpoint to your Cloud SQL instance."]
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using GCP Private Service Connect to connect Hightouch to my Cloud SQL instance.",
          linkText: "reach out"
        }), " to our customer success team to get started with GCP Private Service Connect for Cloud SQL."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more",
        children: "Learn more"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://cloud.google.com/vpc/docs/private-service-connect",
            children: "What is Private Service Connect?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://cloud.google.com/sql/docs/mysql/configure-private-service-connect",
            children: "Configure Private Service Connect - MySQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://cloud.google.com/sql/docs/postgres/configure-private-service-connect",
            children: "Configure Private Service Connect - PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://cloud.google.com/sql/docs/sqlserver/configure-private-service-connect",
            children: "Configure Private Service Connect - SQL Server"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
